import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api.js";
import { useT } from "../lib/i18n.jsx";
import { useToast } from "../components/Toast.jsx";
import ActiveSessionsBadge from "../components/ActiveSessionsBadge.jsx";

export default function AdminDevicesPage() {
  const { t } = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | online | offline
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [connectBusy, setConnectBusy] = useState(null); // device_id being connected
  const [passwordPrompt, setPasswordPrompt] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const res = await api("/devices");
      setDevices(res.devices || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function startAdminConnect(device, password) {
    setConnectBusy(device.device_id);
    try {
      const body = { device_id: device.device_id };
      if (password) body.password = password;
      const r = await api("/browser/connect/start", {
        method: "POST",
        body,
      });
      if (r.requires_2fa) {
        toast.error("2FA erforderlich — bitte über 'My Devices' verbinden");
        return;
      }
      setPasswordPrompt(null);
      navigate(`/connect/${encodeURIComponent(r.session_token)}/${device.device_id}`);
    } catch (err) {
      // Device requires a password — show prompt
      if (err.status === 401 && err.details?.error === "password_required") {
        setPasswordPrompt({
          device,
          kind: err.details.password_kind,
          wrong: !!password,
        });
        return;
      }
      toast.error("Connect fehlgeschlagen: " + err.message);
    } finally {
      setConnectBusy(null);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return devices.filter((d) => {
      if (filter === "online" && !d.online) return false;
      if (filter === "offline" && d.online) return false;
      if (!s) return true;
      return (
        d.device_name?.toLowerCase().includes(s) ||
        d.hostname?.toLowerCase().includes(s) ||
        d.os?.toLowerCase().includes(s) ||
        d.owner_name?.toLowerCase().includes(s) ||
        d.device_id?.toLowerCase().includes(s)
      );
    });
  }, [devices, search, filter]);

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/devices/${deleteTarget.id}`, { method: "DELETE" });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="ph">
        <div>
          <div className="eyebrow">Fleet</div>
          <h1 className="h1">Alle Geräte</h1>
        </div>
        <div className="ph-meta">
          {devices.filter((d) => d.online).length} / {devices.length} online
        </div>
      </div>

      <div className="toolbar" style={{ flexWrap: "wrap" }}>
        <div className="search" style={{ flex: 1, minWidth: 240 }}>
          <span className="search-icon">⌕</span>
          <input
            type="text"
            placeholder="Suche nach Name, Owner, OS, UUID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {["all", "online", "offline"].map((f) => (
            <button
              key={f}
              className={`btn ghost ${filter === f ? "primary" : ""}`}
              onClick={() => setFilter(f)}
              style={{ padding: "8px 14px", fontSize: 11 }}
            >
              {f === "all" ? "Alle" : f === "online" ? "Online" : "Offline"}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-line" style={{ marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div className="loading">Lade Geräte…</div>
      ) : filtered.length === 0 ? (
        <div className="empty">
          <div className="empty-title">Keine Geräte gefunden</div>
          <div className="empty-body">
            {search ? "Versuche eine andere Suche." : "Noch keine Geräte registriert."}
          </div>
        </div>
      ) : (
        <div style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          overflow: "hidden",
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--line)" }}>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Gerät</th>
                <th style={thStyle}>Owner</th>
                <th style={thStyle}>OS</th>
                <th style={thStyle}>Hostname</th>
                <th style={thStyle}>Last Seen</th>
                <th style={thStyle}>Verbindungen</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr key={d.id} style={{ borderBottom: "1px solid var(--line)" }}>
                  <td style={tdStyle}>
                    <span className={`chip ${d.online ? "ok" : ""}`}>
                      <span className={`dot ${d.online ? "live" : "dead"}`} />
                      {d.online ? "online" : "offline"}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600 }}>{d.device_name || "(unnamed)"}</div>
                    <div className="mono" style={{ fontSize: 10, color: "var(--text-faint)" }}>
                      {d.device_id?.slice(0, 12)}…
                    </div>
                  </td>
                  <td style={tdStyle}>
                    {d.owner_name || <span style={{ color: "var(--text-faint)" }}>—</span>}
                  </td>
                  <td style={tdStyle}>
                    <span className="mono" style={{ fontSize: 11 }}>{d.os || "—"}</span>
                  </td>
                  <td style={tdStyle}>
                    <span className="mono" style={{ fontSize: 11 }}>{d.hostname || "—"}</span>
                  </td>
                  <td style={tdStyle}>
                    <span className="mono" style={{ fontSize: 11, color: "var(--text-dim)" }}>
                      {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : "nie"}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, position: "relative" }}>
                    {d.online ? (
                      <ActiveSessionsBadge deviceUuid={d.device_id} />
                    ) : (
                      <span style={{ fontSize: 10, color: "var(--text-faint)" }}>—</span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      <button
                        className="btn primary"
                        style={{ padding: "5px 10px", fontSize: 11 }}
                        disabled={!d.online || connectBusy === d.device_id}
                        onClick={() => startAdminConnect(d)}
                        title={d.online ? "Connect to this device" : "Device offline"}
                      >
                        {connectBusy === d.device_id ? "…" : "Connect"}
                      </button>
                      <button
                        className="btn ghost danger"
                        style={{ padding: "5px 10px", fontSize: 11 }}
                        onClick={() => setDeleteTarget(d)}
                      >
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={overlayStyle}
            onClick={() => !busy && setDeleteTarget(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={modalStyle}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="eyebrow" style={{ color: "var(--err)" }}>Achtung</div>
              <h2 style={{ fontSize: 20, margin: "8px 0 12px" }}>Gerät löschen?</h2>
              <p style={{ color: "var(--text-dim)", fontSize: 13, marginBottom: 18 }}>
                <strong style={{ color: "var(--text)" }}>{deleteTarget.device_name}</strong> wird unwiderruflich gelöscht.
                Alle Sessions, Zuweisungen und Browser-Access-Daten dieses Geräts werden ebenfalls entfernt.
              </p>
              <div className="mono" style={{ fontSize: 10, color: "var(--text-faint)", marginBottom: 18 }}>
                Owner: {deleteTarget.owner_name || "—"} <br />
                UUID: {deleteTarget.device_id}
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn ghost" onClick={() => setDeleteTarget(null)} disabled={busy}>
                  Abbrechen
                </button>
                <button className="btn danger" onClick={confirmDelete} disabled={busy}>
                  {busy ? "Lösche…" : "Endgültig löschen"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {passwordPrompt && (
          <AdminPasswordPromptModal
            device={passwordPrompt.device}
            kind={passwordPrompt.kind}
            wrong={passwordPrompt.wrong}
            onCancel={() => setPasswordPrompt(null)}
            onSubmit={(pw) => startAdminConnect(passwordPrompt.device, pw)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function AdminPasswordPromptModal({ device, kind, wrong, onCancel, onSubmit }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function handle(e) {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    await onSubmit(password);
    setBusy(false);
  }

  const subtitle =
    kind === "device"
      ? "This device is password-protected. Enter the device password to continue."
      : "Enter the password to continue.";

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.94, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg)", border: "1px solid var(--line)",
          borderRadius: 8, padding: 22, minWidth: 380, maxWidth: 460,
          boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
        }}
      >
        <form onSubmit={handle}>
          <h2 style={{ fontSize: 16, marginBottom: 4 }}>Password required</h2>
          <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 6 }}>
            {device.device_name}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-mute)", marginBottom: 16, lineHeight: 1.5 }}>
            {subtitle}
          </div>
          <input
            type="password" autoFocus value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" disabled={busy}
            style={{
              width: "100%", padding: "10px 12px",
              background: "var(--bg-elev)",
              border: wrong ? "1px solid var(--err)" : "1px solid var(--line)",
              borderRadius: "var(--r)", color: "var(--text)",
              fontSize: 13, fontFamily: "var(--mono)", marginBottom: 6,
            }}
          />
          {wrong && (
            <div style={{ fontSize: 11, color: "var(--err)", marginBottom: 12 }}>
              Wrong password — try again.
            </div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
            <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>Cancel</button>
            <button type="submit" className="btn primary" disabled={busy || !password}>
              {busy ? "Verifying…" : "Connect"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "12px 16px",
  fontFamily: "var(--mono)",
  fontSize: 10,
  color: "var(--text-dim)",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  fontWeight: 500,
};
const tdStyle = {
  padding: "12px 16px",
  fontSize: 13,
};
const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.7)",
  backdropFilter: "blur(8px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 100,
  padding: 20,
};
const modalStyle = {
  background: "var(--bg-elev)",
  border: "1px solid var(--err)",
  borderRadius: "var(--r-lg)",
  padding: 32,
  maxWidth: 460,
  width: "100%",
  boxShadow: "0 32px 80px rgba(0,0,0,0.8)",
};
